import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Sprout } from "lucide-react";
import { motion } from "framer-motion";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const AUTH_IMG =
  "https://images.unsplash.com/photo-1659822887922-c1386185cc6b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxmcmVzaCUyMG9yZ2FuaWMlMjB2ZWdldGFibGVzJTIwYmFza2V0fGVufDB8fHx8MTc4MzgwMjI4MHww&ixlib=rb-4.1.0&q=85";

export default function Auth() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get("ref") || undefined;
  const [mode, setMode] = useState(refCode ? "register" : "login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register, forgotPassword, user } = useAuth();
  const navigate = useNavigate();

  // Reactive navigation: once user is authenticated, go to dashboard.
  // Avoids race between setUser() and navigate() within the submit handler.
  useEffect(() => {
    if (user && mode !== "forgot") {
      navigate("/app", { replace: true });
    }
  }, [user, mode, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setBusy(true);
    try {
      if (mode === "forgot") {
        const res = await forgotPassword(email);
        setNotice(res.message);
      } else if (mode === "login") {
        await login(email, password);
      } else {
        await register(name, email, password, refCode);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Visual side */}
      <div className="relative hidden lg:block overflow-hidden">
        <img src={AUTH_IMG} alt="Légumes frais" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[hsl(222,47%,11%)]/80 via-[hsl(142,71%,25%)]/45 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <Sprout className="h-6 w-6" />
            <span className="font-display font-extrabold text-2xl tracking-tight">FoodFend</span>
          </div>
          <div className="max-w-md">
            <p className="font-display italic text-3xl xl:text-4xl leading-tight">
              {t("auth.quote")}
            </p>
            <p className="mono text-xs tracking-mono uppercase mt-6 text-white/70">
              {t("landing.footerTagline")}
            </p>
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-6 sm:p-12 paper-noise">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm relative z-10"
        >
          <div className="flex items-center justify-between mb-8">
            <div className="lg:hidden flex items-center gap-2">
              <Sprout className="h-6 w-6 text-primary" />
              <span className="font-display font-extrabold text-2xl tracking-tight">FoodFend</span>
            </div>
            <div className="ml-auto"><LanguageSwitcher compact /></div>
          </div>

          <p className="mono text-xs tracking-mono uppercase text-muted-foreground mb-2">
            {mode === "login" ? t("auth.back") : mode === "forgot" ? t("auth.recover") : t("auth.welcome")}
          </p>
          <h1 className="font-display text-4xl mb-8">
            {mode === "login" ? t("auth.login") : mode === "forgot" ? t("auth.forgot") : t("auth.register")}
          </h1>

          {mode === "register" && refCode && (
            <div className="mb-6 -mt-4 rounded-2xl border border-primary/25 bg-primary/[0.06] px-3 py-2 text-sm text-primary" data-testid="ref-invite-note">
              {t("auth.refWelcome")}
            </div>
          )}

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="name">{t("auth.firstName")}</Label>
                <Input
                  id="name"
                  data-testid="auth-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Camille"
                  required
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Input
                id="email"
                type="email"
                data-testid="auth-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </div>
            {mode !== "forgot" && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">{t("auth.password")}</Label>
                  {mode === "login" && (
                    <button
                      type="button"
                      data-testid="forgot-password-link"
                      className="text-xs text-primary hover:underline"
                      onClick={() => { setMode("forgot"); setError(""); setNotice(""); }}
                    >
                      {t("auth.forgotShort")}
                    </button>
                  )}
                </div>
                <Input
                  id="password"
                  type="password"
                  data-testid="auth-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>
            )}

            {error && (
              <p className="text-sm text-destructive" data-testid="auth-error">
                {error}
              </p>
            )}
            {notice && (
              <p className="text-sm text-primary" data-testid="auth-notice">
                {notice}
              </p>
            )}

            <Button
              type="submit"
              className="w-full rounded-full"
              disabled={busy}
              data-testid="auth-submit-btn"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {mode === "login" ? t("auth.loginBtn") : mode === "forgot" ? t("auth.sendLink") : t("auth.signup")}
            </Button>
          </form>

          {mode === "forgot" ? (
            <p className="text-sm text-muted-foreground mt-6">
              <button
                type="button"
                data-testid="back-to-login-btn"
                className="text-primary font-semibold hover:underline"
                onClick={() => { setMode("login"); setError(""); setNotice(""); }}
              >
                ← {t("nav.backToLogin")}
              </button>
            </p>
          ) : (
            <p className="text-sm text-muted-foreground mt-6">
              {mode === "login" ? t("auth.noAccount") : t("auth.haveAccount")}
              <button
                type="button"
                data-testid="auth-toggle-btn"
                className="text-primary font-semibold hover:underline"
                onClick={() => {
                  setMode(mode === "login" ? "register" : "login");
                  setError("");
                  setNotice("");
                }}
              >
                {mode === "login" ? t("auth.signupLink") : t("auth.loginLink")}
              </button>
            </p>
          )}

          {mode === "login" && (
            <p className="mono text-xs text-muted-foreground/70 mt-8 border-t border-border pt-4">
              {t("auth.demoAccount")} : demo@rescape.fr / rescape123
            </p>
          )}
        </motion.div>
      </div>
    </div>
  );
}
